import {PencilLine} from 'lucide-react';
import {Tooltip} from '@/matic/components/ui/tooltip';

type WriteBackFieldMarkerProps = {
  /** Human-readable source, e.g. "Cetec". */
  readonly sourceLabel: string;
  readonly onClick: () => void;
};

/**
 * The actionable counterpart to `SyncedFieldMarker`.
 *
 * Same lock, different resolution. `SyncedFieldMarker` labels a field the
 * sync owns and says an edit wouldn't stick; this labels a field the sync
 * owns *and can write back*, so the edit does stick — it goes to the ERP
 * first and MATIC's copy follows.
 *
 * The input it sits under stays disabled, and this is the affordance
 * instead. That is deliberate: the record form submits every active field
 * on save, so an enabled input would fire an ERP write for a field the
 * user never touched, and the confirm-diff round trip would have to be
 * interleaved with the form's own validation lifecycle. A dedicated
 * per-field control keeps the write path explicit and makes the two-pipe
 * model legible — this value lives in the ERP, here is how you change it
 * there.
 */
export function WriteBackFieldMarker({
  sourceLabel,
  onClick,
}: WriteBackFieldMarkerProps) {
  return (
    <Tooltip
      content={
        <span className="block max-w-[16rem]">
          {sourceLabel} is the system of record for this field. Edit it here and
          we&apos;ll write the change back to {sourceLabel}, then refresh
          MATIC&apos;s copy.
        </span>
      }
    >
      <button
        type="button"
        onClick={onClick}
        className="inline-flex items-center gap-1 rounded border border-neutral-200 bg-white px-1.5 py-0.5 text-[11px] font-medium text-neutral-600 transition-colors hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700"
      >
        <PencilLine className="h-3 w-3" aria-hidden="true" />
        Edit in {sourceLabel}
      </button>
    </Tooltip>
  );
}
